import { ComponentType, useState } from 'react'

import {
  Archive,
  ArrowDownToLine,
  Copy,
  Pencil,
  Play,
  RotateCcwKey,
  ScrollText,
  SlidersHorizontal,
  Users,
  ZapIcon,
} from 'lucide-react'

import { ArchiveAccountDialog } from '~/components/account-detail/AccountActionsMenu/ArchiveAccountDialog'
import { EditSignersDialog } from '~/components/account-detail/AccountActionsMenu/EditSignersDialog'
import { RenameAccountDialog } from '~/components/account-detail/AccountActionsMenu/RenameAccountDialog'
import { ResetSignersDialog } from '~/components/account-detail/AccountActionsMenu/ResetSignersDialog'
import { AutomationPolicyDisplay } from '~/components/automations/AutomationPolicyDisplay'
import { useTriggerAutomation } from '~/components/automations/useAutomationActions'
import { DropdownItem, Toggle } from '~/components/component-library'
import {
  Dialog,
  SplitsDialogContent,
} from '~/components/component-library/Dialog'
import { EnableAutoEarnDialogContent } from '~/components/forms/earn/EnableAutoEarnDialog'
import { useAutoEarnToggle } from '~/components/forms/earn/useAutoEarnToggle'
import { WalletConnectIcon } from '~/components/smart-accounts/SmartAccountWalletConnect'
import { useAuth } from '~/context/AuthProvider'
import { useHasPendingMerkleTransactionsForSmartAccountId } from '~/hooks/merkle-transactions/usePendingMerkleTransactions'
import { useActiveOrg, useActiveOrgId, useActiveOrgMember } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { OrgSmartAccount, SmartAccountRole } from '~/types'
import { Automation } from '~/types/automations'
import { copyToClipboard } from '~/utils/copyToClipboard'
import { sendTrackingData } from '~/utils/tracking'
import { isAutoEarnEnabled, supportsAutoEarn } from '~/utils/vault'

type AccountActionDialog =
  'archive' | 'editSigners' | 'enableAutoEarn' | 'rename' | 'resetSigners'

// Every dialog renders inside the shared Dialog shell below
const ACTION_DIALOGS: Record<
  AccountActionDialog,
  ComponentType<{ smartAccount: OrgSmartAccount; closeDialog: () => void }>
> = {
  archive: ArchiveAccountDialog,
  editSigners: EditSignersDialog,
  enableAutoEarn: EnableAutoEarnDialogContent,
  rename: RenameAccountDialog,
  resetSigners: ResetSignersDialog,
}

export const useAccountActionItems = ({
  smartAccount,
  automation,
  isPage,
  onConnectToApps,
}: {
  smartAccount: OrgSmartAccount
  automation?: Automation
  // Gates page-only query work off the table, where it runs per row
  isPage: boolean
  // Provided (page surface) when WalletConnect is available for this account.
  onConnectToApps?: () => void
}) => {
  const [openDialog, setOpenDialog] = useState<AccountActionDialog | null>(null)

  const { setGlobalModalState } = useAuth()
  const { user: authUser } = useAuthUser()
  const activeOrgId = useActiveOrgId()
  const { featureFlags } = useActiveOrg()
  const orgMember = useActiveOrgMember()
  const isOwner = orgMember?.role === 'OWNER'

  const isPrimaryAccount = smartAccount.role === SmartAccountRole.TREASURY
  const isAutoEarnOn = isAutoEarnEnabled(smartAccount)

  // Signer-gated by the toggle hook, matching the getting-started surface.
  // Auto Earn state only where the item renders. Workspace auto-earn replaces
  // this toggle with the one in Settings > General.
  const showsAutoEarn =
    isPage &&
    supportsAutoEarn(smartAccount) &&
    !featureFlags?.['workspace-auto-earn']
  const {
    handleToggle: toggleAutoEarn,
    isDisabled: isAutoEarnToggleDisabled,
    disabledReason: autoEarnDisabledReason,
    pendingEnabled,
  } = useAutoEarnToggle(smartAccount, { enabled: showsAutoEarn })
  const isAutoEarnChecked = isAutoEarnOn || pendingEnabled === true
  // Page only — ArchiveAccountDialog re-checks on the table
  const { hasPendingTransactions } =
    useHasPendingMerkleTransactionsForSmartAccountId(smartAccount.id, {
      enabled: isPage && isOwner,
    })
  const { trigger: triggerAutomation, isTriggering } = useTriggerAutomation(
    automation?.id,
  )

  const numActiveSigners = smartAccount.signers.filter(
    (signer) => !signer.isArchived,
  ).length
  const notEnoughActiveSigners = numActiveSigners < smartAccount.threshold

  const autoEarn: DropdownItem | null = showsAutoEarn
    ? {
        icon: <ZapIcon />,
        content: (
          <>
            <span className="flex-1">
              Auto earn
              <span className="sr-only">
                : {isAutoEarnChecked ? 'on' : 'off'}
                {!isAutoEarnToggleDisabled &&
                  (isAutoEarnOn ? ', select to disable' : ', select to enable')}
              </span>
            </span>
            <Toggle
              isChecked={isAutoEarnChecked}
              isDisabled={isAutoEarnToggleDisabled}
              size="base"
            />
          </>
        ),
        disabled: isAutoEarnToggleDisabled,
        tooltip: isAutoEarnToggleDisabled ? autoEarnDisabledReason : undefined,
        onClick: () => {
          if (isAutoEarnOn) {
            void toggleAutoEarn(false)
          } else {
            setOpenDialog('enableAutoEarn')
          }
        },
      }
    : null

  const deposit: DropdownItem = {
    icon: <ArrowDownToLine />,
    content: 'Deposit...',
    href: { path: `/deposit?account=${smartAccount.address}` },
  }

  const connectToApps: DropdownItem | null = onConnectToApps
    ? {
        icon: <WalletConnectIcon />,
        content: 'Connect to apps...',
        onClick: () => {
          sendTrackingData('clickedConnectWalletConnect', {
            smartAccountId: smartAccount.id,
            smartAccountAddress: smartAccount.address,
            smartAccountOrgId: smartAccount.orgId,
            authUserId: authUser?.id,
            activeOrgId,
          })
          onConnectToApps()
        },
      }
    : null

  const copyAddress: DropdownItem = {
    icon: <Copy />,
    content: 'Copy address',
    onClick: () => copyToClipboard(smartAccount.address, 'Address copied'),
  }

  const rename: DropdownItem | null = isOwner
    ? {
        icon: <Pencil />,
        content: 'Rename...',
        onClick: () => setOpenDialog('rename'),
      }
    : null

  const editSigners: DropdownItem | null = isOwner
    ? {
        icon: <Users />,
        content: 'Edit signers...',
        disabled: notEnoughActiveSigners,
        tooltip: notEnoughActiveSigners
          ? 'Not enough active signers to meet the approval threshold'
          : undefined,
        onClick: () => setOpenDialog('editSigners'),
      }
    : null

  const archive: DropdownItem | null = isOwner
    ? {
        icon: <Archive />,
        content: 'Archive...',
        disabled: isPrimaryAccount || hasPendingTransactions,
        tooltip: isPrimaryAccount
          ? "The primary account can't be archived"
          : hasPendingTransactions
            ? 'Resolve pending transactions before archiving'
            : undefined,
        onClick: () => {
          sendTrackingData('clickedRemoveSmartAccount', {
            smartAccountId: smartAccount.id,
          })
          setOpenDialog('archive')
        },
      }
    : null

  const resetSigners: DropdownItem | null = isOwner
    ? {
        icon: <RotateCcwKey />,
        content: 'Reset signers...',
        onClick: () => setOpenDialog('resetSigners'),
      }
    : null

  const triggerRun: DropdownItem = {
    icon: <Play />,
    content: 'Trigger',
    disabled: !automation?.isActive || isTriggering,
    tooltip: !automation?.isActive
      ? 'This automation is inactive'
      : isTriggering
        ? 'Trigger in progress...'
        : undefined,
    onClick: () => void triggerAutomation(),
  }

  const showRules: DropdownItem = {
    icon: <ScrollText />,
    content: 'Show rules',
    disabled: !automation,
    onClick: () => {
      if (!automation) return
      setGlobalModalState({
        isOpen: true,
        content: (
          <SplitsDialogContent
            id="automationPolicy"
            title="Policy"
            variant="info"
          >
            <AutomationPolicyDisplay policy={automation.policy} />
          </SplitsDialogContent>
        ),
      })
    },
  }

  const editRules: DropdownItem = {
    icon: <SlidersHorizontal />,
    content: 'Edit rules...',
    disabled: !automation,
    href: automation
      ? { path: `/automations/${automation.id}/edit` }
      : undefined,
  }

  const closeDialog = () => setOpenDialog(null)
  const OpenDialogContent = openDialog ? ACTION_DIALOGS[openDialog] : null

  const dialogs = OpenDialogContent && (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) closeDialog()
      }}
    >
      <OpenDialogContent
        smartAccount={smartAccount}
        closeDialog={closeDialog}
      />
    </Dialog>
  )

  return {
    items: {
      autoEarn,
      connectToApps,
      deposit,
      copyAddress,
      rename,
      editSigners,
      archive,
      resetSigners,
      triggerRun,
      showRules,
      editRules,
    },
    dialogs,
  }
}
